import React, { useEffect, useState } from 'react';
import { X, Paperclip } from 'lucide-react';
import { STATUS_OPTIONS, StatusBadge } from './StatusBadge';
import { adminFetch, AuthError } from './adminApi';
import { QuoteReplyPanel } from './QuoteReplyPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HIDDEN = new Set(['id', 'status', 'status_history', 'created_at', 'updated_at', 'uploaded_files', 'files_count', 'answers', 'contact', 'service', 'quote', 'website', 'form_started_at']);

const humanize = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const Rows = ({ rows }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
    <tbody>
      {rows.map(([label, value]) => (
        <tr key={label} style={{ borderBottom: '1px solid var(--border-light)' }}>
          <td style={{ padding: '0.5rem 0.6rem', color: 'var(--text-secondary)', width: '40%', verticalAlign: 'top' }}>{label}</td>
          <td style={{ padding: '0.5rem 0.6rem', fontWeight: '600', whiteSpace: 'pre-wrap' }}>{String(value)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Block = ({ title, children }) => (
  <div style={{ marginBottom: '1.25rem' }}>
    <h4 style={{ margin: '0 0 0.5rem', color: 'var(--brand-primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</h4>
    {children}
  </div>
);

const present = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);

const QuestionnaireAnswers = ({ record, schema }) => {
  if (!schema) return null;
  return schema.sections.map((section) => {
    const rows = [];
    const repeaters = [];
    section.fields.forEach((f) => {
      const v = record.answers[f.key];
      if (!present(v)) return;
      if (f.type === 'repeater') {
        v.forEach((item, i) => repeaters.push([`${f.label} ${i + 1}`, f.fields.filter((s) => present(item[s.key])).map((s) => [s.label, item[s.key]])]));
      } else rows.push([f.label, v]);
    });
    if (!rows.length && !repeaters.length) return null;
    return (
      <Block key={section.title} title={section.title}>
        {rows.length > 0 && <Rows rows={rows} />}
        {repeaters.map(([title, r]) => <div key={title} style={{ marginTop: '0.75rem', paddingLeft: '0.75rem', borderLeft: '3px solid var(--brand-primary)' }}><strong style={{ fontSize: '0.85rem' }}>{title}</strong><Rows rows={r} /></div>)}
      </Block>
    );
  });
};

export const InboxDetail = ({ item, onClose, onStatusChanged, onAuthError }) => {
  const [record, setRecord] = useState(null);
  const [schemas, setSchemas] = useState(null);
  const [status, setStatus] = useState(item.status);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    setRecord(null); setStatus(item.status); setNote(''); setMessage('');
    adminFetch(`/api/inbox/items/${item.kind}/${item.id}`).then((r) => r.json()).then((d) => setRecord(d.record))
      .catch((e) => { if (e instanceof AuthError) onAuthError(); });
  }, [item.kind, item.id, item.status, onAuthError]);

  useEffect(() => {
    if (item.kind === 'questionnaire' && !schemas) fetch(`${BACKEND_URL}/api/quote-forms`).then((r) => r.json()).then((d) => setSchemas(d.services));
  }, [item.kind, schemas]);

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      const res = await adminFetch(`/api/inbox/items/${item.kind}/${item.id}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, note: note || null })
      });
      const data = await res.json();
      if (res.ok) { setMessage('Status saved and client emailed.'); setNote(''); onStatusChanged(data.summary); }
      else setMessage(data.detail || 'Unable to save.');
    } catch (e) {
      if (e instanceof AuthError) onAuthError(); else setMessage('Unable to save.');
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (index, filename) => {
    try {
      const res = await adminFetch(`/api/inbox/items/${item.kind}/${item.id}/files/${index}`);
      if (!res.ok) { setMessage('File not available.'); return; }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { if (e instanceof AuthError) onAuthError(); }
  };

  const generalRows = record ? Object.entries(record).filter(([k, v]) => !HIDDEN.has(k) && present(v) && typeof v !== 'object').map(([k, v]) => [humanize(k), v]) : [];
  const contactRows = record && record.contact ? Object.entries(record.contact).filter(([, v]) => present(v)).map(([k, v]) => [humanize(k), v]) : [];

  return (
    <aside data-testid="inbox-detail" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', padding: '1.5rem', position: 'sticky', top: '110px', maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h3 className="heading-3" style={{ margin: '0 0 0.35rem', fontSize: '1.2rem' }}>#{item.id} · {item.service_label}</h3>
          <StatusBadge status={item.status} />
        </div>
        <button type="button" onClick={onClose} data-testid="inbox-detail-close" aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
      </div>

      <div style={{ padding: '1rem', borderRadius: '10px', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', marginBottom: '1.5rem' }}>
        <label htmlFor="inbox-status-select" style={{ display: 'block', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.4rem' }}>Update status</label>
        <select id="inbox-status-select" data-testid="inbox-status-select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-medium)', fontFamily: 'inherit', marginBottom: '0.6rem' }}>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <textarea data-testid="inbox-status-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note included in the email to the client" style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-medium)', fontFamily: 'inherit', marginBottom: '0.6rem' }} />
        <button type="button" onClick={save} disabled={saving} data-testid="inbox-status-save" className="btn-primary" style={{ width: '100%', padding: '0.7rem' }}>
          {saving ? 'Saving...' : 'Save & Email Client'}
        </button>
        {message && <p data-testid="inbox-status-message" style={{ margin: '0.6rem 0 0', fontSize: '0.85rem', color: message.startsWith('Status saved') ? '#2f855a' : '#c53030' }}>{message}</p>}
      </div>

      <QuoteReplyPanel item={item} onSent={onStatusChanged} onAuthError={onAuthError} />
      {record && record.quote && <p data-testid="inbox-quote-sent" style={{ margin: '-0.75rem 0 1.25rem', fontSize: '0.85rem', color: '#2f855a' }}>Quote on file: ${Number(record.quote.amount).toFixed(2)} · sent {fmt(record.quote.sent_at)}</p>}
      {!record && <p style={{ color: 'var(--text-secondary)' }}>Loading details...</p>}
      {record && (
        <>
          {contactRows.length > 0 && <Block title="Contact"><Rows rows={contactRows} /></Block>}
          {item.kind === 'questionnaire' && <QuestionnaireAnswers record={record} schema={schemas && schemas[record.service]} />}
          {generalRows.length > 0 && <Block title={item.kind === 'order' ? 'Order details' : 'Details'}><Rows rows={generalRows} /></Block>}
          {present(record.uploaded_files) && (
            <Block title="Attachments">
              <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.9rem' }}>
                {record.uploaded_files.map((f, i) => (
                  <li key={`${f.filename}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Paperclip size={14} />
                    {f.storage_path
                      ? <button type="button" data-testid={`inbox-file-${i}`} onClick={() => downloadFile(i, f.filename)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' }}>{f.filename}</button>
                      : <span>{f.filename} <em style={{ color: 'var(--text-secondary)' }}>(emailed only)</em></span>}
                    <span style={{ color: 'var(--text-secondary)' }}>({Math.round(f.size / 1024)} KB)</span>
                  </li>
                ))}
              </ul>
            </Block>
          )}
          {present(record.status_history) && (
            <Block title="History">
              <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                {record.status_history.slice().reverse().map((h, i) => <li key={i}><strong>{(STATUS_OPTIONS.find((s) => s.value === h.status) || {}).label || h.status}</strong> · {fmt(h.at)}{h.note ? ` · "${h.note}"` : ''}</li>)}
              </ul>
            </Block>
          )}
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>Received {fmt(record.created_at)}</p>
        </>
      )}
    </aside>
  );
};
